/* oxlint-disable no-restricted-imports */
/* oxlint-disable no-console -- log */
// Forked component from @medplum/react
// https://github.com/medplum/medplum/blob/main/packages/react/src/ResourceForm/ResourceForm.tsx
// commit: 62901ef

import { Alert, Group } from '@mantine/core'
import {
  AccessPolicyInteraction,
  applyDefaultValuesToResource,
  canWriteResourceType,
  isPopulated,
  satisfiedAccessPolicy,
  tryGetProfile,
} from '@medplum/core'
import type { OperationOutcome, Reference, Resource } from '@medplum/fhirtypes'
import { BackboneElementInput, useMedplum, useResource } from '@medplum/react'
import * as accessPolicies from '@seen/core/models/accessPolicies'
import { Button } from '@seen/ui'
import { IconAlertCircle } from '@tabler/icons-react'
import { type FormEvent, type JSX, useEffect, useMemo, useState } from 'react'

export interface ResourceFormProps<
  T extends Resource = Resource,
  K extends Extract<keyof T, string> = Extract<keyof T, string>,
> {
  readonly defaultValue: Partial<T> | Reference<T> // TODO: Support union types
  readonly outcome?: OperationOutcome
  readonly onSubmit: (resource: T) => void
  readonly onDelete?: (resource: T) => void
  readonly schemaName?: string
  /** (optional) URL of the resource profile used to display the form. Takes priority over schemaName. */
  readonly profileUrl?: string
  readonly visibleFields?: K[]
  readonly hiddenFields?: K[]
  readonly readonlyFields?: K[]
  readonly loading?: boolean
  readonly submitButtonText?: string
}

export function ResourceForm<T extends Resource>(
  props: ResourceFormProps<T>
): JSX.Element {
  const { outcome } = props
  const medplum = useMedplum()
  const defaultValue = useResource<T>(props.defaultValue)
  const [schemaLoaded, setSchemaLoaded] = useState<string>()
  const [value, setValue] = useState<T>()
  const accessPolicy = medplum.getAccessPolicy()

  useEffect(() => {
    if (defaultValue) {
      if (props.profileUrl) {
        const profileUrl: string = props.profileUrl
        medplum
          .requestProfileSchema(props.profileUrl, { expandProfile: true })
          .then(() => {
            const profile = tryGetProfile(profileUrl)
            if (profile) {
              setSchemaLoaded(profile.name)
              const modifiedDefaultValue = applyDefaultValuesToResource(
                defaultValue,
                profile
              ) as T
              setValue(modifiedDefaultValue)
            } else {
              console.error(`Schema not found for ${profileUrl}`)
            }
          })
          .catch((reason) => {
            console.error('Error in requestProfileSchema', reason)
          })
      } else {
        const schemaName = props.schemaName ?? defaultValue?.resourceType
        medplum
          .requestSchema(schemaName)
          .then(() => {
            setValue(defaultValue)
            setSchemaLoaded(schemaName)
          })
          .catch(console.log)
      }
    }
  }, [medplum, defaultValue, props.schemaName, props.profileUrl])

  // ##### START OF MODIFICATION #####
  const accessPolicyResource = useMemo(
    () =>
      defaultValue &&
      accessPolicies.mergeAccessPolicyResource(
        satisfiedAccessPolicy(
          defaultValue,
          AccessPolicyInteraction.READ,
          accessPolicy
        ),
        accessPolicies.buildAccessPolicyResource(
          defaultValue,
          props.visibleFields,
          props.hiddenFields,
          props.readonlyFields
        )
      ),
    [accessPolicy, defaultValue, props]
  ) // schemaLoaded to recompute when tryGetDataType is ready
  // ##### END OF MODIFICATION #####

  const canWrite = useMemo<boolean>(() => {
    if (medplum.isSuperAdmin()) {
      return true
    }

    if (!accessPolicy) {
      return true
    }

    if (!isPopulated(value?.resourceType)) {
      return true
    }

    return canWriteResourceType(accessPolicy, value?.resourceType)
  }, [medplum, accessPolicy, value?.resourceType])

  if (!schemaLoaded || !value) {
    return <div>Loading...</div>
  }

  if (!canWrite) {
    return (
      <Alert color="red" title="Permission denied" icon={<IconAlertCircle />}>
        Your access level prevents you from editing and creating{' '}
        {value.resourceType} resources.
      </Alert>
    )
  }

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (props.onSubmit) {
          props.onSubmit(value)
        }
      }}
    >
      <BackboneElementInput
        path={value.resourceType}
        valuePath={value.resourceType}
        typeName={schemaLoaded}
        defaultValue={value}
        outcome={outcome}
        onChange={setValue}
        profileUrl={props.profileUrl}
        accessPolicyResource={accessPolicyResource}
      />
      <Group justify="flex-end" mt="xl">
        <Button loading={props.loading} type="submit">
          {props.submitButtonText ?? 'Submit'}
        </Button>
        {props.onDelete && (
          <Button
            loading={props.loading}
            variant="outline"
            color="red"
            type="button"
            onClick={() => {
              if (props.onDelete) {
                props.onDelete(value)
              }
            }}
          >
            Delete
          </Button>
        )}
      </Group>
    </form>
  )
}
